/**
 * tenant-modules — the tenant's deployed modules, from getTenantProfile (CC 0130).
 *
 * Read once per session and shared. `null` means unknown: still loading, the read
 * failed, this user may not make it, or the deployment hasn't recorded its modules.
 * Callers keep today's behaviour on null, and hide a surface only when the list is
 * KNOWN and lacks the module.
 *
 * getTenantProfile is read under branding.read (the module list is not a settings secret: event roles
 * need it to know which surfaces the tenant composes; CC, 2026-10-04). It is requested, its answer is
 * accepted, and a cached answer is handed out only while someone is signed in, the
 * tenant is CONFIRMED (getConfirmedTenantSlug() is null during a pending workspace
 * switch, so nothing is requested for either tenant while the chrome shows the
 * incoming one), and the person the app is acting as holds branding.read. That is
 * checked when the read starts, when its answer lands and whenever the answer is
 * read. Otherwise the answer is null and nothing is cached, so an administrator who
 * signs in later in the same page still gets a read.
 *
 * Each answer is filed under the session it was read for: the confirmed tenant, the
 * tenant generation, the signed-in and acted-as identity, the capability, and an
 * epoch that moves whenever any of those changes or a switch starts. An earlier
 * session's read neither fills the cache nor reaches a hook. The fence is a
 * module-level access subscription, so sign-out, a new sign-in, View as and
 * capability changes clear the answer even when no screen that uses it is mounted.
 *
 * Carries the platform-side tenant fence forward (drop-2026-1002c): a switch purges
 * synchronously and requests nothing; the confirmed change re-reads for mounted users.
 *
 * `useTenantModulesState` (2026-10-20) is the same read with "unknown" split out, for a screen
 * that must draw nothing module-shaped until it knows: pending, waiting, denied, error, malformed
 * or ready. It is stricter than `useTenantModules`: an answer whose module list is missing or
 * holds anything but strings is malformed, where the legacy hook keeps the strings.
 * `useTenantModules`, `readTenantModules`, `tenantModulesNow` and `moduleKnownAbsent` are unchanged.
 */
import { useEffect, useState } from "react";
import { getTenantProfile } from "@v0y4ge/api-client";
import { studioRequestOptions } from "./studio-config";
import {
  can, getConfirmedTenantSlug, getImpersonation, getRealUser, getTenantGeneration, getUser,
  isSignedIn, onTenantChange, onTenantSwitching, subscribeAccess,
} from "../shell/access";

/** The capability getTenantProfile is authorised under (branding.read since CC, 2026-10-04; it was
 *  settings.read, which no event role holds, so their module list stayed unknown). */
const PROFILE_CAPABILITY = "branding.read";

type Modules = string[] | null;
type Read = { scope: string; promise: Promise<Modules> };
/** How a settled read ended: a well-formed module list, a refused or failed request, or an answer
 *  whose module list is missing or not all strings. Kept beside the legacy value, which still
 *  filters, so the two hooks can differ on a mixed answer without either changing the other. */
type Outcome = "ready" | "error" | "malformed";

let epoch = 0;
let cached: { scope: string; value: Modules; outcome: Outcome } | undefined;
let pending: Read | null = null;
const listeners = new Set<() => void>();

/** Everything an answer belongs to, apart from the epoch. */
function sessionFingerprint(): string {
  const real = getRealUser();
  const acting = getUser();
  const viewAs = getImpersonation();
  return JSON.stringify([
    isSignedIn(), getConfirmedTenantSlug(), getTenantGeneration(),
    real.id ?? "", real.email ?? "", acting.id ?? "", acting.email ?? "",
    viewAs ? viewAs.startedAt : 0, can(PROFILE_CAPABILITY),
  ]);
}

export type TenantProfileAccess =
  | { kind: "denied" }
  | { kind: "waiting" }
  | { kind: "allowed"; scope: string };

/** Whether the current session may read the tenant profile right now. `denied`:
 *  signed out, or the acted-as user lacks branding.read. `waiting`: no confirmed
 *  tenant yet (first load, or a workspace switch in flight). `allowed` carries the
 *  scope a read must still match when its answer lands. */
export function tenantProfileAccess(): TenantProfileAccess {
  if (!isSignedIn() || !can(PROFILE_CAPABILITY)) return { kind: "denied" };
  if (getConfirmedTenantSlug() === null) return { kind: "waiting" };
  return { kind: "allowed", scope: `${epoch}:${sessionFingerprint()}` };
}

function currentScope(): string | null {
  const access = tenantProfileAccess();
  return access.kind === "allowed" ? access.scope : null;
}

function emit(): void { listeners.forEach((fn) => fn()); }

function invalidate(): void {
  epoch++;
  cached = undefined;
  pending = null;
  // Told every time, not only when an answer was held: a status hook must leave denied or
  // waiting the moment the session changes, before any read has started. A legacy hook
  // re-reads the same null and React keeps its state.
  emit();
}

let lastFingerprint = sessionFingerprint();
let reloadQueued = false;

/** Re-read for mounted users once the current notification has finished. Never
 *  requests while the session is denied or waiting. */
function reloadSoon(): void {
  if (reloadQueued || !listeners.size) return;
  reloadQueued = true;
  queueMicrotask(() => {
    reloadQueued = false;
    if (listeners.size && currentScope() !== null) void load();
  });
}

function fence(): void {
  const fingerprint = sessionFingerprint();
  if (fingerprint === lastFingerprint) return;
  lastFingerprint = fingerprint;
  invalidate();
  reloadSoon();
}

subscribeAccess(fence);
onTenantSwitching(() => { invalidate(); lastFingerprint = sessionFingerprint(); });
onTenantChange(fence);

function parseModules(r: unknown): Modules {
  const p = ((r as { data?: unknown })?.data ?? r) as { modules?: unknown } | null;
  return p && Array.isArray(p.modules) ? (p.modules as unknown[]).filter((x): x is string => typeof x === "string") : null;
}

/** True only for an answer that carries a modules array of strings (empty included). */
function wellFormed(r: unknown): boolean {
  const p = ((r as { data?: unknown })?.data ?? r) as { modules?: unknown } | null;
  return !!p && Array.isArray(p.modules) && (p.modules as unknown[]).every((x) => typeof x === "string");
}

function settle(read: Read, value: Modules, outcome: Outcome): Modules {
  if (pending === read) pending = null;
  if (currentScope() !== read.scope) return null;
  cached = { scope: read.scope, value, outcome };
  emit();
  return value;
}

function load(): Promise<Modules> {
  const scope = currentScope();
  if (scope === null) return Promise.resolve(null);
  if (cached?.scope === scope) return Promise.resolve(cached.value);
  if (pending?.scope === scope) return pending.promise;
  let request: Promise<unknown>;
  try { request = getTenantProfile(studioRequestOptions("nodes.tenants") as never) as Promise<unknown>; }
  catch (e) { request = Promise.reject(e); }
  const read: Read = { scope, promise: Promise.resolve(null) };
  read.promise = request.then(
    (r) => settle(read, parseModules(r), wellFormed(r) ? "ready" : "malformed"),
    () => settle(read, null, "error"),
  );
  pending = read;
  return read.promise;
}

/** The answer for this session, reading it if needed. Null when unknown (denied, waiting, failed or not recorded). */
export function readTenantModules(): Promise<string[] | null> { return load(); }

/** The current answer, without a read. Null unless it was read for this session. */
export function tenantModulesNow(): string[] | null {
  const scope = currentScope();
  return scope !== null && cached?.scope === scope ? cached.value : null;
}

/** True only when the module list is known and lacks `module`. */
export function moduleKnownAbsent(module: string, mods: string[] | null = tenantModulesNow()): boolean {
  return Array.isArray(mods) && !mods.includes(module);
}

/** Where the module list stands for this session. `modules` is the list only when `ready`
 *  (possibly empty); every other status carries null, and null never means "all modules".
 *  - pending   allowed, and this session's read has not answered yet
 *  - waiting   no confirmed tenant (first load, or a workspace switch in flight)
 *  - denied    signed out, or the acted-as user lacks branding.read
 *  - error     the read was refused or failed
 *  - malformed the answer's module list was missing or held something other than strings */
export type TenantModulesStatus = "pending" | "waiting" | "denied" | "error" | "malformed" | "ready";
export interface TenantModulesState { readonly status: TenantModulesStatus; readonly modules: string[] | null }

/** The current status, without a read. Fenced like `tenantModulesNow`: an answer filed under an
 *  earlier tenant, identity, View as, capability or epoch is never reported for this one. */
export function tenantModulesStateNow(): TenantModulesState {
  const access = tenantProfileAccess();
  if (access.kind !== "allowed") return { status: access.kind, modules: null };
  if (cached?.scope !== access.scope) return { status: "pending", modules: null };
  return { status: cached.outcome, modules: cached.outcome === "ready" ? cached.value : null };
}

/** Ask again after an error or malformed answer: that answer is dropped for this session (every
 *  status hook shows pending) and one read starts; a second call while it runs joins it. A ready
 *  answer is kept, and a denied or waiting session requests nothing. */
export function retryTenantModules(): Promise<Modules> {
  const scope = currentScope();
  if (scope !== null && cached?.scope === scope && cached.outcome !== "ready") {
    cached = undefined;
    emit();
  }
  return load();
}

/** `{ status, modules, retry }`, read from the module state at RENDER time: a render that runs
 *  before the subscription has fired (a tenant switch, sign-out or View as in the same commit)
 *  still sees the current session, never a previous session's ready answer. */
export function useTenantModulesState(): TenantModulesState & { readonly retry: () => void } {
  const [, setTick] = useState(0);
  useEffect(() => {
    let live = true;
    const push = () => { if (live) setTick((n) => n + 1); };
    listeners.add(push);
    push();
    void load().then(push);
    return () => { live = false; listeners.delete(push); };
  }, []);
  return { ...tenantModulesStateNow(), retry: retryTenantModules };
}

export function useTenantModules(): string[] | null {
  const [m, setM] = useState<string[] | null>(() => tenantModulesNow());
  useEffect(() => {
    let live = true;
    const push = () => { if (live) setM(tenantModulesNow()); };
    listeners.add(push);
    push();
    void load().then(push);
    return () => { live = false; listeners.delete(push); };
  }, []);
  return m;
}
